import React, { ReactNode } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import Header from './Header'
import Footer from './Footer'
import { Button } from '@/components/ui/button'
import { FileQuestion, FileText, BarChart2, Users, BookOpen, LogOut, User } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'

interface AdminLayoutProps {
  children: ReactNode
}

const AdminLayout = ({ children }: AdminLayoutProps) => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { toast } = useToast()

  const isActive = (path: string) => {
    return location.pathname === path
  }

  const handleLogout = async () => {
    await logout()
    toast({
      title: 'Çıkış Yapıldı',
      description: 'Başarıyla çıkış yapıldı.',
    })
    navigate('/admin/login')
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      <div className="flex-1 flex flex-col md:flex-row">
        {/* Sidebar */}
        <aside className="bg-gray-50 border-r w-full md:w-64 p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-xl font-bold">Admin Panel</h2>
          </div>

          {/* User Info */}
          {user && (
            <div
              className="mb-6 p-3 bg-gray-100 rounded-md cursor-pointer hover:bg-gray-200 transition-colors"
              onClick={() => navigate('/admin/password-update')}
            >
              <div className="flex items-center gap-2 mb-2">
                <User className="h-4 w-4 text-gray-500" />
                <span className="text-sm font-medium">Kullanıcı</span>
              </div>
              <p className="text-xs text-gray-600 truncate">{user.email}</p>
              <p className="text-xs text-blue-600 mt-1">Şifreyi güncellemek için tıklayın</p>
            </div>
          )}

          <nav className="space-y-2">
            <Link
              to="/admin/questions"
              className={`flex items-center gap-2 p-2 rounded-md ${
                isActive('/admin/questions')
                  ? 'bg-interview text-white'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FileQuestion size={18} />
              <span>Sorular</span>
            </Link>

            <Link
              to="/admin/results"
              className={`flex items-center gap-2 p-2 rounded-md ${
                isActive('/admin/results')
                  ? 'bg-interview text-white'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              <FileText size={18} />
              <span>Sonuçlar</span>
            </Link>

            <Link
              to="/admin/analytics"
              className={`flex items-center gap-2 p-2 rounded-md ${
                isActive('/admin/analytics')
                  ? 'bg-interview text-white'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              <BarChart2 size={18} />
              <span>Soru Analizi</span>
            </Link>

            <Link
              to="/admin/employees"
              className={`flex items-center gap-2 p-2 rounded-md ${
                location.pathname.startsWith('/admin/employees')
                  ? 'bg-interview text-white'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Users size={18} />
              <span>Çalışanlar</span>
            </Link>

            <Link
              to="/admin/tests"
              className={`flex items-center gap-2 p-2 rounded-md ${
                location.pathname.startsWith('/admin/tests')
                  ? 'bg-interview text-white'
                  : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              <BookOpen size={18} />
              <span>Sınavlar</span>
            </Link>
          </nav>

          {/* Logout Button */}
          <div className="mt-8 pt-4 border-t">
            <Button variant="outline" className="w-full" onClick={handleLogout}>
              <LogOut size={16} className="mr-2" />
              Çıkış Yap
            </Button>
          </div>
        </aside>

        {/* Main content */}
        <main className="flex-1 p-6">{children}</main>
      </div>

      <Footer />
    </div>
  )
}

export default AdminLayout
